import { useState, useCallback, useEffect } from 'react'
import { updatesApi, ApiError } from '@/lib/updates-api'
import { attendanceApi } from '@/lib/attendance-api'
import { trackEvent, Events } from '@/lib/observability'
import { MANUAL_UPDATE_TYPES, UPDATE_TYPE_LABELS, type UpdateType, type Update } from '@/types/update'
import type { Child } from '@/types/child'
import type { UploadedImage } from '@/types/image'
import { ImageUploader } from '@/components/images'

interface AddUpdateFormProps {
  children: Child[]
  preselectedChildIds?: string[]
  editUpdate?: Update // If provided, form is in edit mode
  onSuccess: (update: Update) => void
  onCancel?: () => void
  filterCheckedInOnly?: boolean // If true, only show children who are currently checked in
}

export function AddUpdateForm({
  children,
  preselectedChildIds = [],
  editUpdate,
  onSuccess,
  onCancel,
  filterCheckedInOnly = false,
}: AddUpdateFormProps) {
  const isEditMode = !!editUpdate
  // Filter out locked children - they can't have updates added
  const allAvailableChildren = children.filter((c) => !c.isLocked)
  const [checkedInChildIds, setCheckedInChildIds] = useState<string[] | null>(null)
  const [checkedInLoading, setCheckedInLoading] = useState(filterCheckedInOnly)

  // Load checked-in children if filtering is enabled
  useEffect(() => {
    if (filterCheckedInOnly) {
      setCheckedInLoading(true)
      attendanceApi.getCheckedIn()
        .then(({ children: checkedIn }) => {
          setCheckedInChildIds(checkedIn.map(c => c.id))
        })
        .catch(err => {
          console.error('Failed to load checked-in children:', err)
          // On error, fall back to showing all children
          setCheckedInChildIds(null)
        })
        .finally(() => {
          setCheckedInLoading(false)
        })
    }
  }, [filterCheckedInOnly])

  // Apply checked-in filter if enabled
  const availableChildren = filterCheckedInOnly && checkedInChildIds
    ? allAvailableChildren.filter(c => checkedInChildIds.includes(c.id))
    : allAvailableChildren

  const [type, setType] = useState<UpdateType>(editUpdate?.type || 'note')
  const [content, setContent] = useState(editUpdate?.content || '')
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>(
    editUpdate?.children?.map((c) => c.id) || preselectedChildIds.filter((id) => availableChildren.some((c) => c.id === id))
  )
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleImagesChange = useCallback((images: UploadedImage[]) => {
    setUploadedImages(images)
  }, [])

  const handleToggleChild = (childId: string) => {
    setSelectedChildIds((prev) =>
      prev.includes(childId)
        ? prev.filter((id) => id !== childId)
        : [...prev, childId]
    )
  }

  const handleSelectAll = () => {
    setSelectedChildIds(availableChildren.map((c) => c.id))
  }

  const handleSelectNone = () => {
    setSelectedChildIds([])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (selectedChildIds.length === 0) {
      setError('Please select at least one child')
      return
    }

    if (!content.trim()) {
      setError('Please enter some content')
      return
    }

    setIsLoading(true)
    try {
      let response: { update: Update }

      if (isEditMode && editUpdate) {
        // Update existing
        response = await updatesApi.update(editUpdate.id, {
          type,
          content: content.trim(),
          childIds: selectedChildIds,
        })
      } else {
        // Create new
        response = await updatesApi.create({
          type,
          content: content.trim(),
          childIds: selectedChildIds,
          imageIds: uploadedImages.map((img) => img.id),
        })
        // Track update creation (no content, just metadata)
        trackEvent(Events.UPDATE_CREATED, {
          updateType: type,
          childCount: selectedChildIds.length,
          hasImages: uploadedImages.length > 0,
          imageCount: uploadedImages.length,
        })
        // Only reset form on create
        setContent('')
        setType('note')
        setUploadedImages([])
        if (!preselectedChildIds.length) {
          setSelectedChildIds([])
        }
      }

      onSuccess(response.update)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : isEditMode ? 'Failed to update' : 'Failed to add update')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div>
        <label className="mb-2 block text-sm font-medium">Update Type</label>
        <div className="flex flex-wrap gap-2">
          {MANUAL_UPDATE_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`rounded-full px-3 py-1 text-sm ${
                type === t
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              disabled={isLoading}
            >
              {UPDATE_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="content" className="mb-1 block text-sm font-medium">
          {type === 'food' && 'What did they eat?'}
          {type === 'toilet' && 'Toilet details'}
          {type === 'activity' && 'What activity?'}
          {type === 'medicine' && 'Medicine details'}
          {type === 'sleep' && 'Sleep details'}
          {type === 'note' && 'Note'}
        </label>
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          className="w-full rounded border border-gray-300 px-3 py-2"
          placeholder={
            type === 'food' ? 'e.g., Ate all their lunch - pasta and vegetables'
            : type === 'toilet' ? 'e.g., Nappy change at 2pm'
            : type === 'activity' ? 'e.g., Painted a picture of their family'
            : type === 'medicine' ? 'e.g., Calpol 5ml at 2pm for temperature'
            : type === 'sleep' ? 'e.g., Napped from 1pm to 2:30pm'
            : 'Enter your note here...'
          }
          disabled={isLoading}
        />
      </div>

      {/* Image upload - only show when creating new update */}
      {!isEditMode && (
        <ImageUploader
          onImagesChange={handleImagesChange}
          disabled={isLoading}
        />
      )}

      {(!preselectedChildIds.length || isEditMode) && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium">Select Children</label>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-primary-600 hover:underline"
                disabled={isLoading}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={handleSelectNone}
                className="text-gray-500 hover:underline"
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {availableChildren.map((child) => (
              <button
                key={child.id}
                type="button"
                onClick={() => handleToggleChild(child.id)}
                className={`rounded-full px-3 py-1 text-sm ph-no-capture ${
                  selectedChildIds.includes(child.id)
                    ? 'bg-primary-100 text-primary-800 ring-2 ring-primary-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                disabled={isLoading}
              >
                {child.firstName} {child.lastName}
              </button>
            ))}
          </div>
          {checkedInLoading && (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
          {!checkedInLoading && availableChildren.length === 0 && filterCheckedInOnly && (
            <p className="text-sm text-gray-500">No children are currently checked in. Check in children from the Attendance page first.</p>
          )}
          {!checkedInLoading && availableChildren.length === 0 && !filterCheckedInOnly && (
            <p className="text-sm text-gray-500">No children available</p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isLoading || selectedChildIds.length === 0}
          className="btn-primary"
        >
          {isLoading ? (isEditMode ? 'Saving...' : 'Adding...') : (isEditMode ? 'Save Changes' : 'Add Update')}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
